import React, { useState, useMemo, useEffect, useId } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import NavBar from '../pages/NavBar/NavBar';
import Button from '../common/Button';
import Dropdown from '../../utils/components/Dropdown';
import ReactDataGrid from '@inovua/reactdatagrid-community';
import '@inovua/reactdatagrid-community/index.css';
import { toast } from 'react-toastify';
import FooterData from "../pages/NavBar/FooterData";
import { checkUserAuthentication, handleUnauthenticatedUser } from "../../utils/authUtils";
import { getIdMeBaseApiUrl } from "../../utils/UrlConstants";
import { useMsal } from '@azure/msal-react';
import { useIdentityAppService } from '../../services/IdentityServices';

interface RequestHistoryResponse {
    success: boolean;
    message: string;
    requests: UserAppRequestDto[];
    totalCount: number;
    timestamp: string;
}

interface UserAppRequestDto {
    id: number;
    userId: number;
    userLogin: string;
    firstName: string;
    lastName: string;
    userEmail: string;
    applicationCode: string;
    applicationName: string;
    roleCode: string;
    roleName: string;
    assignedTo: string;
    status: string;
    operationalFlag: string;
    createdDate: string;
    modifiedDate: string;
    notes: string;
}

const RequestHistory: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { accounts } = useMsal();

    // State for data fetching
    const [requests, setRequests] = useState<UserAppRequestDto[]>([]);
    const [assignedTo, setAssignedTo] = useState<string>('');
    const [totalCount, setTotalCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);

    // Get user type and search state from navigation state
    const userType: string = location.state?.userType || 'External';
    const searchState = location.state?.searchState;

    useEffect(() => {
        if (!checkUserAuthentication()) {
            handleUnauthenticatedUser();
            return;
        }
    }, []);

    // Effect to fetch request history data
    useEffect(() => {
        const fetchRequestHistory = async () => {
            if (!accounts[0]?.name) {
                toast.error('Unable to determine user identity for request history');
                return;
            }

            try {
                setLoading(true);

                const assignedToUser = accounts[0].idTokenClaims?.oid || accounts[0].name;
                const response = await useIdentityAppService().fetchRequestHistoryByAssignedUser(assignedToUser);

                if (response.ok) {
                    const data: RequestHistoryResponse = await response.json();
                    setRequests(data.requests);
                    setAssignedTo(assignedToUser);
                    setTotalCount(data.totalCount);
                } else {
                    const errorData = await response.json();
                    toast.error(errorData.message || 'Failed to fetch request history');
                }
            } catch (error: any) {
                console.error('Error fetching request history:', error);
                toast.error('Network error occurred while fetching request history');
            } finally {
                setLoading(false);
            }
        };

        // Only fetch if we have accounts
        if (accounts && accounts.length > 0) {
            fetchRequestHistory();
        }
    }, [accounts]);

    // Filter states
    const [selectedApplication, setSelectedApplication] = useState<string>('');
    const [selectedStatus, setSelectedStatus] = useState<string>('');

    // Get unique applications and statuses from requests
    const applicationChoices = useMemo(() => {
        const uniqueApps = Array.from(new Set(
            requests
                .filter(req => req.applicationName && req.applicationName.trim() !== '')
                .map(req => req.applicationName)
        )).sort();

        return uniqueApps.map(app => ({ value: app, label: app }));
    }, [requests]);

    const statusChoices = useMemo(() => {
        const uniqueStatuses = Array.from(new Set(
            requests
                .filter(req => req.status && req.status.trim() !== '')
                .map(req => req.status)
        )).sort();

        return uniqueStatuses.map(status => ({ value: status, label: status }));
    }, [requests]);

    // Filter requests based on selected filters
    const filteredRequests = useMemo(() => {
        return requests.filter(request => {
            const matchesApplication = !selectedApplication || request.applicationName === selectedApplication;
            const matchesStatus = !selectedStatus || request.status === selectedStatus;
            return matchesApplication && matchesStatus;
        });
    }, [requests, selectedApplication, selectedStatus]);

    // Handle filter changes
    const handleApplicationChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedApplication(event.target.value);
    };

    const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedStatus(event.target.value);
    };

    // Clear all filters
    const handleClearFilters = () => {
        setSelectedApplication('');
        setSelectedStatus('');
    };

    const handleViewDetails = (request: UserAppRequestDto) => {
        // Navigate to request details page with request data
        navigate('/request-details', {
            state: {
                request: request,
                assignedTo: assignedTo,
                // Pass back info to return to this page
                returnData: {
                    requests: requests,
                    assignedTo: assignedTo,
                    totalCount: totalCount
                }
            }
        });
    };

    const handleBack = () => {
        navigate('/admin-dashboard', { state: { userTypeSelection: userType, searchState } });
    };

    React.useEffect(() => {
        if (!loading && !requests.length) {
            toast.error('No request history data found. Redirecting to User Management.');
            navigate('/admin-dashboard', { state: { userTypeSelection: userType, searchState } });
        }
    }, [loading, requests.length, navigate, userType, searchState]);

    const requestGridColumns = [
        {
            name: 'id',
            header: 'Request ID',
            defaultWidth: 100,
            type: 'number',
        },
        {
            name: 'userLogin',
            header: 'Username',
            defaultFlex: 1,
            minWidth: 120,
        },
        {
            name: 'firstName',
            header: 'First Name',
            defaultFlex: 1,
            minWidth: 100,
        },
        {
            name: 'lastName',
            header: 'Last Name',
            defaultFlex: 1,
            minWidth: 100,
        },
        {
            name: 'applicationName',
            header: 'Application',
            defaultFlex: 1,
            minWidth: 150,
        },
        {
            name: 'roleName',
            header: 'Role',
            defaultFlex: 1,
            minWidth: 150,
        },
        {
            name: 'status',
            header: 'Status',
            defaultWidth: 100,
            render: ({ value }: { value: string }) => {
                const getStatusStyles = (status: string) => {
                    switch (status?.toUpperCase()) {
                        case 'APPROVED':
                            return {
                                backgroundColor: '#E8F5E8',
                                color: '#4CAF50'
                            };
                        case 'REJECTED':
                            return {
                                backgroundColor: '#FFEBEE',
                                color: '#f44336'
                            };
                        case 'REQUESTED':
                            return {
                                backgroundColor: '#fff3cd',
                                color: '#856404'
                            };
                        case 'ERROR':
                            return {
                                backgroundColor: '#FFF3E0',
                                color: '#FF9800'
                            };
                        default:
                            return {
                                backgroundColor: '#f5f5f5',
                                color: '#666'
                            };
                    }
                };

                const statusStyles = getStatusStyles(value);

                return (
                    <span style={{
                        padding: '3px 8px',
                        borderRadius: '4px',
                        backgroundColor: statusStyles.backgroundColor,
                        color: statusStyles.color,
                        fontSize: '12px',
                        fontWeight: 'bold'
                    }}>
                        {value}
                    </span>
                );
            }
        },
        {
            name: 'createdDate',
            header: 'Created Date',
            defaultWidth: 130,
            render: ({ value }: { value: string }) => {
                return value ? new Date(value).toLocaleDateString() : '';
            }
        },
        {
            name: 'actions',
            header: 'Actions',
            defaultWidth: 120,
            render: ({ data }: { data: UserAppRequestDto }) => (
                <Button
                    onClick={() => handleViewDetails(data)}
                >
                    Details
                </Button>
            ),
            sortable: false,
            filterable: false
        }
    ];

    // Get display title with filter information
    const getDisplayTitle = () => {
        const baseTitle = `Requests (${filteredRequests.length}${filteredRequests.length !== requests.length ? ` of ${requests.length}` : ''})`;

        const activeFilters = [];
        if (selectedApplication) activeFilters.push(`App: ${selectedApplication}`);
        if (selectedStatus) activeFilters.push(`Status: ${selectedStatus}`);

        if (activeFilters.length > 0) {
            return `${baseTitle} - Filtered by: ${activeFilters.join(', ')}`;
        }

        return baseTitle;
    };

    return (
        <React.Fragment>
            <NavBar />

            <div style={{ marginLeft: '20px', marginRight: '20px', minHeight: '300px' }}>
                <div className="subheading grid-row">
                    <div className="grid-col flex-1">
                        <h3 className="subheading-title" style={{
                            textAlign: 'center',
                            marginBottom: '20px',
                            marginTop: '40px'
                        }}>
                            Request History
                        </h3>
                    </div>
                </div>

                <div style={{
                    padding: '20px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    margin: '0 auto',
                    maxWidth: '1200px',
                    marginTop: '20px'
                }}>
                    {!loading && (
                        <>
                            <div style={{
                                marginBottom: '30px',
                                padding: '15px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '6px',
                                border: '1px solid #dee2e6'
                            }}>
                                <p style={{ margin: '5px 0', fontSize: '16px', fontWeight: 'bold', color: '#00607f' }}>
                                    Assigned To: {assignedTo}
                                </p>
                                <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                                    Total Requests: {totalCount}
                                </p>
                            </div>

                            {/* Filter Section */}
                            <div style={{
                                marginBottom: '30px',
                                padding: '20px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '6px',
                                border: '1px solid #dee2e6'
                            }}>
                                <h5 style={{ margin: '0 0 15px 0', color: '#00607f' }}>
                                    Filter Requests
                                </h5>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: '15px',
                                    alignItems: 'end'
                                }}>
                                    <div>
                                        <Dropdown
                                            choices={applicationChoices}
                                            emptyChoiceLabel="All Applications"
                                            label="Filter by Application"
                                            name="applicationFilter"
                                            value={selectedApplication}
                                            onChange={handleApplicationChange}
                                        />
                                    </div>
                                    <div>
                                        <Dropdown
                                            choices={statusChoices}
                                            emptyChoiceLabel="All Statuses"
                                            label="Filter by Status"
                                            name="statusFilter"
                                            value={selectedStatus}
                                            onChange={handleStatusChange}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <Button
                                            onClick={handleClearFilters}
                                            disabled={!selectedApplication && !selectedStatus}
                                        >
                                            Clear Filters
                                        </Button>
                                    </div>
                                </div>

                                {/* Filter Summary */}
                                {(selectedApplication || selectedStatus) && (
                                    <div style={{
                                        marginTop: '15px',
                                        padding: '10px',
                                        backgroundColor: '#e3f2fd',
                                        borderRadius: '4px',
                                        border: '1px solid #90caf9'
                                    }}>
                                        <div style={{ fontSize: '14px', color: '#1565c0' }}>
                                            <strong>Active Filters:</strong>
                                            {selectedApplication && <span style={{ marginLeft: '8px' }}>Application: <strong>{selectedApplication}</strong></span>}
                                            {selectedApplication && selectedStatus && <span style={{ margin: '0 4px' }}>•</span>}
                                            {selectedStatus && <span>Status: <strong>{selectedStatus}</strong></span>}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {!loading && filteredRequests.length > 0 ? (
                        <div>
                            <h4 style={{ marginBottom: '20px', color: '#333' }}>
                                {getDisplayTitle()}
                            </h4>
                            <ReactDataGrid
                                licenseKey="AppName=CoherenceApp,Company=Coherence,ExpiryDate=2025-04-15T00:00:00.000Z,LicenseDeveloperCount=1,LicenseType=single_app,Ref=CoherenceLicenseRef,Z=983320388-1513273079-1127323721-2081795733-199375339958733905"
                                idProperty="id"
                                pagination
                                columns={requestGridColumns}
                                dataSource={filteredRequests}
                                sortable={true}
                                defaultLimit={10}
                                rowHeight={60}
                                enableColumnAutosize={true}
                                style={{ minHeight: 400 }}
                                defaultSortInfo={[{ name: 'createdDate', dir: -1 }]}
                            />
                        </div>
                    ) : !loading && (
                        <div style={{
                            textAlign: 'center',
                            padding: '60px 20px',
                            color: '#666',
                            fontSize: '16px'
                        }}>
                            <h4 style={{ marginBottom: '15px' }}>
                                {(selectedApplication || selectedStatus) ? 'No Requests Match Your Filters' : 'No Requests Found'}
                            </h4>
                            <p>
                                {(selectedApplication || selectedStatus)
                                    ? 'Try adjusting your filter criteria to see more results.'
                                    : 'You have no role assignment requests at this time.'
                                }
                            </p>
                            {(selectedApplication || selectedStatus) && (
                                <div style={{ marginTop: '20px' }}>
                                    <Button
                                        onClick={handleClearFilters}
                                    >
                                        Clear All Filters
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    {loading && (
                        <div style={{
                            textAlign: 'center',
                            padding: '60px 20px',
                            color: '#666',
                            fontSize: '16px'
                        }}>
                            <h4 style={{ marginBottom: '15px' }}>Loading Request History...</h4>
                            <p>Please wait while we fetch your request history.</p>
                        </div>
                    )}

                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: '30px',
                        paddingTop: '20px',
                        borderTop: '1px solid #eee'
                    }}>
                        <Button onClick={handleBack}>Back to User Management</Button>
                    </div>
                </div>
            </div>
            <FooterData/>
        </React.Fragment>
    );
};

export default RequestHistory;